import { Intent, Tab, Tabs } from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import { Banner } from "components/common/Banner";
import { ScenarioCharts } from "in_queue/components/charts/ScenarioCharts";
import { RibbonSlotContext } from "in_queue/components/tables/ribbon/RibbonSlotContext";
import { ScenarioAllocatedUpgradesTable } from "in_queue/components/tables/ScenarioAllocatedUpgradesTable";
import { ScenarioConstraintsTable } from "in_queue/components/tables/ScenarioConstraintsTable";
import { ScenarioProjectsTable } from "in_queue/components/tables/ScenarioProjectsTable";
import { AdditionalColumnConfigsProvider } from "in_queue/contexts/AdditionalColumnConfigsContext";
import { useCluster } from "in_queue/contexts/ClusterContext";
import {
    useCurrentProjectId,
    useCurrentScenarioMetadata
} from "in_queue/contexts/ScenarioPageContext";
import {
    ScenarioResultsProvider,
    useScenarioResults
} from "in_queue/contexts/ScenarioResultsContext";
import { useChromeHeightVar } from "in_queue/hooks/useChromeHeightVar";
import { isProjectExcluded } from "in_queue/types/scenarioUtils";
import { getCurrentModelSetForStage } from "in_queue/utils/customUpgradeValidation";
import { describeStaleScenario } from "in_queue/utils/staleScenario";
import { useCallback, useEffect, useRef, useState } from "react";
import { ScenarioMetrics } from "../../metrics/ScenarioMetrics";
import { ScenarioAssumptions } from "../../Scenario/ScenarioAssumptions";
import css from "./ScenarioResults.module.scss";

export const ScenarioResults: React.FC = () => {
    return (
        <AdditionalColumnConfigsProvider>
            <ScenarioResultsProvider>
                <div className={css.results}>
                    <ScenarioStaleWarning />
                    <ScenarioDroppedProjectInfo />
                    <ScenarioMetrics />
                </div>
                <AssumptionsPanel />
                <ScenarioChartsWrapper />
                <div className={css.tables}>
                    <ScenarioTables />
                </div>
            </ScenarioResultsProvider>
        </AdditionalColumnConfigsProvider>
    );
};

const AssumptionsPanel: React.FC = () => {
    const scenario = useCurrentScenarioMetadata();
    return <ScenarioAssumptions scenario={scenario} renderInPanel />;
};

const ScenarioStaleWarning: React.FC = () => {
    const scenario = useCurrentScenarioMetadata();
    const cluster = useCluster();
    if (!scenario.isStale) return null;

    // The scenario's own stage, not the cluster's active one: staleness
    // compares against the current models for the stage it ran in.
    const modelSet = getCurrentModelSetForStage(cluster, scenario.stage);

    return (
        <Banner
            intent={Intent.WARNING}
            icon={IconNames.WARNING_SIGN}
            title="These results are stale."
            description={describeStaleScenario(scenario, modelSet)}
        />
    );
};

const ScenarioDroppedProjectInfo: React.FC = () => {
    const projectId = useCurrentProjectId();
    const scenario = useCurrentScenarioMetadata();
    if (!isProjectExcluded(scenario.assumptions[projectId])) return null;
    return (
        <Banner
            intent={Intent.PRIMARY}
            icon={IconNames.INFO_SIGN}
            title="This scenario dropped this project."
            description="Cost-allocation and rank results below are computed as if this project did not exist in the queue for this scenario."
        />
    );
};

const ScenarioChartsWrapper: React.FC = () => {
    const projectId = useCurrentProjectId();
    const { scenarioResults, isLoadingRemaining } = useScenarioResults();
    if (scenarioResults === "loading") return null;
    return (
        <ScenarioCharts
            results={scenarioResults}
            currentProjectId={projectId}
            isFullyLoaded={!isLoadingRemaining}
        />
    );
};

interface ScenarioTableProps {
    filterText: string;
    setFilterText: (value: string) => void;
    stickyHeader?: boolean;
}

const SCENARIO_TABS: ReadonlyArray<{
    id: string;
    title: string;
    Table: React.FC<ScenarioTableProps>;
}> = [
    {
        id: "assigned-upgrades",
        title: "Assigned upgrades",
        Table: ScenarioAllocatedUpgradesTable
    },
    {
        id: "constraints",
        title: "Constraints",
        Table: ScenarioConstraintsTable
    },
    {
        id: "projects",
        title: "Projects",
        Table: ScenarioProjectsTable
    }
];

const ScenarioTables: React.FC = () => {
    const [selectedTabId, setSelectedTabId] = useState<string | number>(
        "assigned-upgrades"
    );
    const hostRef = useRef<HTMLDivElement | null>(null);
    const chromeRef = useRef<HTMLDivElement | null>(null);
    const [ribbonSlot, setRibbonSlot] = useState<HTMLDivElement | null>(null);
    const ribbonSlotRef = useCallback((node: HTMLDivElement | null) => {
        setRibbonSlot(node);
    }, []);

    const [searchByTab, setSearchByTab] = useState<Record<string, string>>({});

    const mountedTabIds = useRef<Set<string>>(new Set());
    mountedTabIds.current.add(String(selectedTabId));

    useChromeHeightVar(hostRef, chromeRef, true);

    useEffect(() => {
        const host = hostRef.current;
        const chrome = chromeRef.current;
        if (host && chrome) {
            const hostTop = host.getBoundingClientRect().top;
            const chromeTop = chrome.getBoundingClientRect().top;
            if (hostTop < chromeTop - 1) {
                host.scrollIntoView({ block: "start" });
            }
        }
        const raf = requestAnimationFrame(() =>
            window.dispatchEvent(new Event("resize"))
        );
        return () => cancelAnimationFrame(raf);
    }, [selectedTabId]);

    return (
        <div ref={hostRef}>
            <div ref={chromeRef} className={css["sticky-chrome"]}>
                <Tabs
                    id="scenario-tables"
                    selectedTabId={selectedTabId}
                    onChange={setSelectedTabId}
                    large
                >
                    {SCENARIO_TABS.map(({ id, title }) => (
                        <Tab key={id} id={id} title={title} />
                    ))}
                </Tabs>
                <div ref={ribbonSlotRef} className={css["ribbon-slot"]} />
            </div>
            {SCENARIO_TABS.map(({ id, Table }) => {
                if (!mountedTabIds.current.has(id)) return null;
                const isActive = id === selectedTabId;
                return (
                    <div
                        key={id}
                        className={css["table-fill"]}
                        style={isActive ? undefined : { display: "none" }}
                    >
                        <RibbonSlotContext.Provider
                            value={isActive ? ribbonSlot : null}
                        >
                            <Table
                                filterText={searchByTab[id] ?? ""}
                                setFilterText={(value) =>
                                    setSearchByTab((prev) => ({
                                        ...prev,
                                        [id]: value
                                    }))
                                }
                                stickyHeader
                            />
                        </RibbonSlotContext.Provider>
                    </div>
                );
            })}
        </div>
    );
};
